import React from 'react';
import { useAIAssistant } from '../AIAssistantProvider';

const EscalationPanel: React.FC = () => {
  const ctx = useAIAssistant();
  const { theme, escalations, escalationsTotal, escalationsLoading } = ctx;

  return (
    <div className="p-6 xl:p-8 flex-1 hover-scroll min-h-0">
      <div className="flex items-center justify-between mb-4">
        <span className={`text-[10px] font-bold ${theme.isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          {escalationsTotal} total
        </span>
        <button
          onClick={() => ctx.fetchEscalations()}
          className={`p-1 rounded-lg transition-colors ${theme.isDark ? 'text-slate-400 hover:text-blue-400 hover:bg-slate-700' : 'text-slate-400 hover:text-blue-500 hover:bg-slate-100'}`}
          title="Refresh"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className={`w-3.5 h-3.5 ${escalationsLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {escalationsLoading && escalations.length === 0 ? (
        <p className={`text-[11px] font-medium ${theme.isDark ? 'text-slate-400' : 'text-slate-500'}`}>Loading...</p>
      ) : escalations.length === 0 ? (
        <div className="text-center py-8">
          <svg xmlns="http://www.w3.org/2000/svg" className={`w-8 h-8 mx-auto mb-3 ${theme.isDark ? 'text-slate-600' : 'text-slate-300'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className={`text-[11px] font-medium ${theme.isDark ? 'text-slate-400' : 'text-slate-500'}`}>No escalations yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {escalations.map(esc => {
            const status = esc.status || (esc.resolved ? 'resolved' : 'pending');
            const isActive = status === 'pending' || status === 'rerouted';
            const isClaimed = status === 'claimed';
            const isResolved = status === 'resolved';

            return (
              <div
                key={esc.id}
                className={`p-4 rounded-xl border transition-all ${
                  isResolved
                    ? theme.isDark ? 'bg-slate-800/50 border-slate-700/50 opacity-50' : 'bg-slate-50 border-slate-100 opacity-50'
                    : status === 'rerouted'
                    ? theme.isDark ? 'bg-red-950/30 border-red-800/50' : 'bg-red-50 border-red-200'
                    : isClaimed
                    ? theme.isDark ? 'bg-amber-950/20 border-amber-800/40' : 'bg-amber-50 border-amber-200'
                    : theme.isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200 shadow-sm'
                }`}
              >
                {/* Status + route badge row */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${
                      status === 'pending' ? 'bg-blue-500 animate-pulse' :
                      status === 'rerouted' ? 'bg-red-500 animate-pulse' :
                      status === 'claimed' ? 'bg-amber-500' :
                      'bg-emerald-500'
                    }`} />
                    <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${
                      esc.route === 'sales' ? 'bg-emerald-100 text-emerald-700' :
                      esc.route === 'technical' ? 'bg-orange-100 text-orange-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {esc.route}
                    </span>
                    {status === 'rerouted' && (
                      <span className="text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">
                        REROUTED
                      </span>
                    )}
                  </div>
                  <span className={`text-[9px] font-bold ${theme.isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {new Date(esc.created_at).toLocaleString()}
                  </span>
                </div>

                {/* Visitor info */}
                <p className={`text-[11px] font-bold truncate ${theme.isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                  {esc.visitor_name !== 'Anonymous' ? esc.visitor_name : esc.visitor_email || 'Anonymous visitor'}
                </p>
                {esc.visitor_email && esc.visitor_name !== 'Anonymous' && (
                  <p className={`text-[10px] truncate ${theme.isDark ? 'text-slate-400' : 'text-slate-500'}`}>{esc.visitor_email}</p>
                )}

                {/* Message preview */}
                <p className={`text-[10px] mt-2 line-clamp-2 ${theme.isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                  {esc.message.replace('[Auto-escalated by AI] Reason: ', '')}
                </p>

                {/* Primary Actions */}
                <div className="flex items-center justify-between mt-3">
                  <span className={`text-[9px] ${theme.isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {esc.routed_to}
                  </span>
                  <div className="flex items-center gap-1.5">
                    {isActive && (
                      <button
                        onClick={() => ctx.claimEscalation(esc.id)}
                        className="text-[9px] font-bold text-blue-600 hover:text-blue-700 px-2 py-1 rounded-lg hover:bg-blue-50 transition-colors"
                      >
                        Claim
                      </button>
                    )}
                    {(isActive || isClaimed) && (
                      <button
                        onClick={() => ctx.resolveEscalation(esc.id)}
                        className="text-[9px] font-bold text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-50 transition-colors"
                      >
                        Resolve
                      </button>
                    )}
                    {isResolved && (
                      <span className="text-[9px] font-bold text-emerald-500">Resolved</span>
                    )}
                  </div>
                </div>

                {/* Transfer + Notes (claimed escalations only) */}
                {isClaimed && (
                  <div className={`mt-2 pt-2 border-t ${theme.isDark ? 'border-slate-600' : 'border-slate-200'}`}>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {['sales', 'technical', 'enquiry'].filter(d => d !== esc.route).map(dept => (
                        <button
                          key={dept}
                          onClick={() => ctx.transferEscalation(esc.id, { to_department: dept })}
                          className={`text-[8px] font-bold px-2 py-0.5 rounded-full transition-colors ${
                            theme.isDark ? 'bg-slate-600 text-slate-300 hover:bg-slate-500' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          → {dept}
                        </button>
                      ))}
                      <button
                        onClick={() => {
                          const note = prompt('Internal note (not visible to visitor):');
                          if (note?.trim()) ctx.addEscalationNote(esc.id, note.trim());
                        }}
                        className={`text-[8px] font-bold px-2 py-0.5 rounded-full transition-colors ${
                          theme.isDark ? 'bg-amber-900/30 text-amber-400 hover:bg-amber-900/50' : 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                        }`}
                      >
                        + Note
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EscalationPanel;
